/**
 * 火山引擎 DataFinder 埋点 SDK（私有化部署版）
 *
 * 接入方式：动态创建 script 标签异步加载 SDK，避免阻塞首屏
 * 使用方式：
 *   import { initVolcAnalytics, trackEvent, setVolcUserId } from '@/utils/volcAnalytics'
 *   initVolcAnalytics({ app_id: 10000025, channel_domain: 'https://snssdk.jtexpress.my' })
 *   trackEvent('button_click', { page: 'home', button: 'submit' })
 *   setVolcUserId('user_123')
 */

export interface VolcInitOptions {
  /** 应用 ID */
  app_id: number
  /** 私有化部署服务地址（SDK 加载域名 & 上报域名） */
  channel_domain: string
  /** 禁止 SDK 自身监控事件 onload 的上报，默认 true */
  disable_sdk_monitor?: boolean
  /** SDK 脚本路径（完整 URL 或相对路径，相对路径拼到 channel_domain 后） */
  sdk_path: string
  /** 全局变量名，默认 collectEvent */
  export_name?: string
}

let initialized = false

/**
 * 初始化火山埋点 SDK
 * 1. 注入全局收集器（TeaAnalyticsObject / collectEvent），保证 SDK 加载前的事件不丢失
 * 2. 异步加载 SDK 脚本
 * 3. 执行 init + start
 */
export function initVolcAnalytics(options: VolcInitOptions): void {
  if (initialized || typeof window === 'undefined') return

  const {
    app_id,
    channel_domain,
    disable_sdk_monitor = true,
    sdk_path,
    export_name = 'collectEvent'
  } = options

  const win = window as any

  // 1. 注入全局收集器（与官方 snippet 等价，先建队列保证事件不丢）
  win['TeaAnalyticsObject'] = export_name
  if (!win[export_name]) {
    // eslint-disable-next-line prefer-rest-params
    const _collect: any = function () {
      _collect.q.push(Array.prototype.slice.call(arguments))
    }
    _collect.q = _collect.q || []
    win[export_name] = _collect
  }
  win[export_name].l = +new Date()

  // 2. 先调用 init / start（SDK 加载前会入队列，加载后自动 flush）
  try {
    const collect = win[export_name]
    collect('init', {
      app_id,
      channel_domain,
      disable_sdk_monitor,
      disable_auto_pv: false,
      disable_heartbeat: true,
      disable_route_report: false,
      disable_session: true,
      log: true, // 调试：临时打开 SDK 日志
    })
    collect('config', {
      platform: 'Yoyi-Site'
    })
    collect('start')
  } catch (e) {
    console.error('[VolcAnalytics] init 调用失败:', e)
  }

  // 3. 异步加载 SDK 脚本
  // sdk_path 支持完整 URL（http 开头直接用）或相对路径（拼到 channel_domain 后）
  const sdkSrc = sdk_path.startsWith('http') ? sdk_path : `${channel_domain}${sdk_path}`
  const script = document.createElement('script')
  script.id = 'volc-analytics-sdk'
  script.async = true
  script.src = sdkSrc

  script.onload = () => {
    initialized = true
    console.log('[VolcAnalytics] SDK 加载成功')
  }

  script.onerror = () => {
    console.error('[VolcAnalytics] SDK 脚本加载失败')
  }

  document.head.appendChild(script)
}

/**
 * 上报自定义事件
 * @param eventName 事件名
 * @param params 事件参数（可选）
 */
export function trackEvent(eventName: string, params?: Record<string, any>): void {
  try {
    const collect = (window as any)['collectEvent']
    if (typeof collect === 'function') {
      collect(eventName, params || {})
    }
  } catch (e) {
    // 静默失败，不影响业务
  }
}

/**
 * 设置用户 ID（登录后调用，关联用户身份）
 * @param userId 用户唯一标识
 */
export function setVolcUserId(userId: string | number | undefined | null): void {
  if (!userId) return
  try {
    const collect = (window as any)['collectEvent']
    if (typeof collect === 'function') {
      collect('config', {
        user_unique_id: String(userId),
      })
    }
  } catch (e) {
    console.error('[VolcAnalytics] 设置用户 ID 失败:', e)
  }
}

/**
 * 设置全局公共属性（所有事件都会自动带上）
 * @param properties 公共属性对象
 */
export function setVolcCommonProperties(properties: Record<string, any>): void {
  try {
    const collect = (window as any)['collectEvent']
    if (typeof collect === 'function') {
      collect('config', {
        header: properties,
      })
    }
  } catch (e) {
    // 静默失败
  }
}

export default initVolcAnalytics
